<script setup lang="ts">
import type { ShortsSectionVariant } from './variants';
import type { ShortVideo } from '~/composables/shorts';
import type {
	FeaturedVideosRecommendationType,
	ShortsFilters
} from '~/core/entities/property/filters';
import type { SectionName } from '~/types/mixpanel';

import { cn, useStaleGuard } from '@/utils';
import {
	breakpointsTailwind,
	useBreakpoints,
	useEventListener,
	useIntersectionObserver
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import {
	useFetchShorts,
	usePropertyIntentPrefetch
} from '~/composables/shorts';
import { useOpenShortsFeed } from '~/composables/shorts/useOpenShortsFeed';
import { useCarouselPreloadWindow } from '~/composables/useCarouselPreloadWindow';
import { SHORTS_HOME_PATH } from '~/composables/useShortsFilters/buildShortsLocationPath';
import {
	hasRecommendationReference,
	withImagesEvery
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';
import {
	prefetchReelPosterOnIntent,
	prefetchVideoOnIntent,
	useCarouselSegmentWarmup,
	warmVideoManifests
} from '~/composables/useVideoWarmup';
import {
	canSpeculate,
	claimWarmSection,
	trySpendWarmBudget,
	WARM_COST_FEED_API
} from '~/composables/useWarmScheduler';
import { MixpanelProps } from '~/core/service/mixpanel';
import { hash } from 'ohash';

import { VideoCard } from '~/components/VideoCard';

import IconMaterialSymbolsSlowMotionVideo from '~icons/material-symbols/slow-motion-video-sharp';

import SectionSkeleton from './SectionSkeleton.vue';
import { getSectionStyles } from './variants';

const DEFAULT_MAX_VIDEOS = 15;
// Página maior só no fluxo de entrada da SRP (`preserveFeedEntryContext`): com
// `includeImagesEvery`, o `per_page` do Heimdall conta o total de itens finais
// (vídeos + imagens), então 17 mantém ~15 vídeos visíveis após esconder as imagens.
const FEED_ENTRY_PER_PAGE = 17;

const props = withDefaults(
	defineProps<{
		title: string;
		filters?: ShortsFilters;
		variant?: ShortsSectionVariant;
		showTitleIcon?: boolean;
		singleCardOnLg?: boolean;
		disablePeekFromLg?: boolean;
		useCompactMobileTopSpacing?: boolean;
		useParentMobileGutter?: boolean;
		hideBottomBorder?: boolean;
		enableRecommendationFallback?: boolean;
		enableRecommendationTypeAnalytics?: boolean;
		preserveFeedEntryContext?: boolean;
		titleSource?: 'prop' | 'api';
		sliderSectionName?: SectionName;
		videoCardSectionName?: SectionName;
		ctaSectionName?: SectionName;
		videoCardPositionMode?: 'absolute' | 'visible';
		fetchPage?: number;
		fetchPerPage?: number;
		maxVideos?: number;
	}>(),
	{
		variant: 'dark',
		showTitleIcon: true,
		singleCardOnLg: false,
		disablePeekFromLg: false,
		useCompactMobileTopSpacing: false,
		useParentMobileGutter: false,
		hideBottomBorder: false,
		enableRecommendationFallback: false,
		enableRecommendationTypeAnalytics: false,
		preserveFeedEntryContext: false,
		titleSource: 'prop',
		sliderSectionName: 'shorts',
		videoCardSectionName: 'shorts hero section',
		ctaSectionName: 'shorts',
		videoCardPositionMode: 'absolute',
		fetchPage: 1,
		maxVideos: DEFAULT_MAX_VIDEOS
	}
);

const styles = computed(() => getSectionStyles(props.variant));
const sectionClass = computed(() =>
	cn(styles.value.section, props.hideBottomBorder && 'border-b-0')
);
const containerClass = computed(() =>
	cn(styles.value.containerClass, props.useCompactMobileTopSpacing && 'pt-4')
);
const carouselItemClass = computed(() =>
	cn(
		styles.value.carouselItemClass,
		props.disablePeekFromLg &&
			'lg:basis-[calc(50%-9px)] xl:basis-[calc(33.33333%-12px)]',
		props.singleCardOnLg && 'lg:basis-full'
	)
);
const usesParentMobileGutter = computed(
	() => props.variant === 'light' && props.useParentMobileGutter
);
const headerClass = computed(() =>
	cn(styles.value.headerClass, usesParentMobileGutter.value && 'px-0')
);
const footerClass = computed(() =>
	cn(styles.value.footerClass, usesParentMobileGutter.value && 'px-0')
);

const carouselOptions = computed(() => ({
	cyclic: true,
	class: styles.value.contentGap,
	carouselWrapperClasses: cn(
		styles.value.carouselWrapperClass,
		usesParentMobileGutter.value && 'px-0'
	)
}));

const emit = defineEmits<{
	(e: 'update:has-videos', value: boolean): void;
	(e: 'update:used-fallback', value: boolean): void;
}>();

const { fetchVideos, prefetchVideos } = useFetchShorts();

const allVideos = ref<ShortVideo[]>([]);
const isLoading = ref(false);
const isPreloaded = ref(false);
const feedHasMore = ref(false);
const feedAllWatched = ref(false);
const appliedFeedFilters = ref<ShortsFilters | undefined>(undefined);
const apiSectionTitle = ref<string | undefined>(undefined);
const recommendationType = ref<FeaturedVideosRecommendationType | undefined>(
	undefined
);

const videos = computed(() =>
	(allVideos.value || [])
		.filter(
			(item): item is ShortVideo & { videoUrl: string } => item.videoUrl != null
		)
		.slice(0, props.maxVideos)
);

const isMobileOrTablet = ref(false);
const sectionRef = ref<HTMLElement | null>(null);
const isSectionVisible = ref(false);

// While the carousel is on screen and the network is provably fast, lazily
// pull the first cards' opening segments in idle time — a tap then starts
// playback from cache. All gating lives in the composable.
// Unique per instance: two shorts sections on one page must arbitrate the
// warm right between themselves, not share a claim.
const warmSectionId = `shorts-section-${useId()}`;

useCarouselSegmentWarmup(
	() => videos.value.map(item => item.videoUrl),
	() => isSectionVisible.value,
	warmSectionId
);

// Same claim/gates as the segment warm, but for the feed LIST: the click on a
// card (or the CTA) fetches featured-videos with these exact filters/page —
// having the response in the prefetch cache removes that round trip from the
// navigation. Fired once per section, in idle, only while visible.
let feedListWarmed = false;
watch(
	isSectionVisible,
	visible => {
		if (!visible || feedListWarmed || !import.meta.client) return;
		const run = () => {
			if (!isSectionVisible.value || feedListWarmed) return;
			if (!canSpeculate() || !claimWarmSection(warmSectionId)) return;
			if (!trySpendWarmBudget(WARM_COST_FEED_API)) return;
			feedListWarmed = true;
			prefetchVideos(
				appliedFeedFilters.value ?? props.filters ?? {},
				props.fetchPage
			);
		};
		if ('requestIdleCallback' in window) {
			requestIdleCallback(run, { timeout: 10_000 });
		} else {
			setTimeout(run, 1000);
		}
	},
	{ immediate: true }
);

// Posters além dos 3 primeiros são lazy nativos — na rolagem rápida o usuário
// alcança cards ainda baixando. Quando a seção se APROXIMA do viewport
// (margem de ~1000px), os primeiros NEAR_POSTER_EAGER_COUNT viram eager e
// chegam prontos. O observer só arma após a primeira interação real
// (scroll/toque/ponteiro): o Lighthouse nunca interage, então o carregamento
// inicial da página fica intocado por construção.
const NEAR_POSTER_EAGER_COUNT = 8;
const isSectionNear = ref(false);
const posterEagerCount = computed(() =>
	isSectionNear.value ? NEAR_POSTER_EAGER_COUNT : undefined
);

function armPosterPreloadOnApproach(): void {
	// O observer nasce dentro do callback do evento — fora do escopo do
	// componente, onde o VueUse NÃO consegue registrar a limpeza automática.
	// Sem este onUnmounted, navegar para outra página entre a 1ª interação e a
	// aproximação da seção reteria o observer (e o subtree observado) para
	// sempre.
	let stopNearObserver: (() => void) | null = null;
	onUnmounted(() => stopNearObserver?.());

	// `once` remove cada listener após o próprio disparo; os `stop` limpam os
	// irmãos que não dispararam — e o useEventListener amarra tudo ao escopo do
	// componente, então um unmount antes da 1ª interação não vaza listener.
	const stops = (['scroll', 'pointermove', 'touchstart'] as const).map(
		eventName =>
			useEventListener(
				window,
				eventName,
				() => {
					stops.forEach(stop => stop());
					if (!sectionRef.value) return;
					const { stop: stopObserver } = useIntersectionObserver(
						sectionRef,
						entries => {
							if (!entries[0]?.isIntersecting) return;
							isSectionNear.value = true;
							stopObserver();
						},
						{ rootMargin: '1000px 0px', threshold: 0 }
					);
					stopNearObserver = stopObserver;
				},
				{ passive: true, once: true }
			)
	);
}

const sliderRef = ref<{
	currentCarouselItemIndex: number;
	scrollTo: (index: number) => void;
} | null>(null);

const shouldAutoplay = computed(
	() => isMobileOrTablet.value && isSectionVisible.value
);

// Swipe-ahead (autoplay mobile): ao trocar o card ativo, aquece a cadeia do
// PRÓXIMO — o swipe seguinte encontra manifesto + 1º segmento no cache e o
// autoplay começa sem a espera visível do download. O card recém-ativo já
// baixa os próprios bytes pelo player; quem precisa de antecipação é o vizinho.
watch(
	() => sliderRef.value?.currentCarouselItemIndex,
	newIndex => {
		if (!shouldAutoplay.value || newIndex == null) return;
		const next = videos.value[newIndex + 1];
		if (next) prefetchVideoOnIntent(next.videoUrl, next.prefetchVariants);
	}
);

const { preloadDistanceFor } = useCarouselPreloadWindow(
	() => sliderRef.value?.currentCarouselItemIndex
);

const hasVideos = computed(() => videos.value.length > 0);

const shouldShowSection = computed(
	() => !isPreloaded.value || isLoading.value || hasVideos.value
);

const displayedTitle = computed(() =>
	props.titleSource === 'api'
		? (apiSectionTitle.value ?? props.title)
		: props.title
);
const feedEntryPerPage = computed(
	() =>
		props.fetchPerPage ??
		(props.preserveFeedEntryContext ? FEED_ENTRY_PER_PAGE : undefined)
);

const fetchGuard = useStaleGuard();

async function loadVideos(filters: ShortsFilters): Promise<void> {
	const capturedId = fetchGuard.next();
	isLoading.value = true;
	// SRP (`preserveFeedEntryContext`): busca a lista completa intercalada (vídeos +
	// imagens) com página maior, para semear o feed. Demais usos mantêm o atual.
	const requestFilters = props.preserveFeedEntryContext
		? withImagesEvery(filters)
		: filters;
	const perPage = feedEntryPerPage.value;
	try {
		const result = await fetchVideos(requestFilters, props.fetchPage, perPage, {
			enableRecommendationFallback: props.enableRecommendationFallback
		});
		if (fetchGuard.isStale(capturedId)) return;
		const appliedFilters = result.appliedFilters ?? requestFilters;
		const usedFallback =
			result.recommendationType === 'fallback' ||
			(props.enableRecommendationFallback &&
				hasRecommendationReference(filters) &&
				Boolean(
					appliedFilters.displayInHomeShorts &&
					!appliedFilters.referenceProperty
				));
		allVideos.value = result.items;
		feedHasMore.value = result.hasMore;
		feedAllWatched.value = result.allWatched ?? false;
		appliedFeedFilters.value = appliedFilters;
		apiSectionTitle.value = result.sectionTitle?.replace(/\.$/, '');
		recommendationType.value = result.recommendationType;
		isPreloaded.value = true;
		warmVideoManifests(result.items.map(item => item.videoUrl));
		emit('update:used-fallback', usedFallback);
		emit(
			'update:has-videos',
			result.items.some(item => item.videoUrl != null)
		);
	} catch {
		if (fetchGuard.isStale(capturedId)) return;
		allVideos.value = [];
		feedHasMore.value = false;
		feedAllWatched.value = false;
		apiSectionTitle.value = undefined;
		recommendationType.value = undefined;
		isPreloaded.value = true;
		emit('update:used-fallback', false);
		emit('update:has-videos', false);
	} finally {
		if (!fetchGuard.isStale(capturedId)) {
			isLoading.value = false;
		}
	}
}

onMounted(() => {
	const breakpoints = useBreakpoints(breakpointsTailwind);
	isMobileOrTablet.value = breakpoints.smaller('lg').value;

	watch(breakpoints.smaller('lg'), val => {
		isMobileOrTablet.value = val;
	});

	useIntersectionObserver(
		sectionRef,
		entries => {
			const entry = entries[0];
			if (entry) {
				isSectionVisible.value = entry.isIntersecting;
			}
		},
		{ threshold: 0.3 }
	);

	requestIdleCallback(() => {
		loadVideos(props.filters ?? {});
	});

	armPosterPreloadOnApproach();
});

const fetchParamsSignature = computed(() =>
	hash([props.filters ?? {}, props.fetchPage, props.fetchPerPage])
);

watch(fetchParamsSignature, () => {
	loadVideos(props.filters ?? {});
});

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();
const { openShortsFeed } = useOpenShortsFeed();
const { prefetchPropertyOnIntent } = usePropertyIntentPrefetch();

// O 1º card é o clique mais provável da seção: o master dele (~500B) entra
// como preload de baixa prioridade no head — o hover/clique economiza um
// round-trip sem competir com LCP nem com o conteúdo da página.
const firstCardMasterUrl = computed(() => {
	const url = videos.value[0]?.videoUrl;
	return url ? getProxiedVideoUrl(url) : null;
});
useHead({
	link: computed(() =>
		firstCardMasterUrl.value
			? [
					{
						rel: 'preload' as const,
						as: 'fetch' as const,
						href: firstCardMasterUrl.value,
						crossorigin: 'anonymous' as const,
						fetchpriority: 'low' as const
					}
				]
			: []
	)
});

// Sustained hover (>=150ms) on desktop is a strong pre-click signal: start
// the property-data request the shorts page will make on navigation. Data
// only — video bytes keep their own intent/speculation rules.
const HOVER_INTENT_MS = 150;
let hoverTimer: ReturnType<typeof setTimeout> | undefined;

function handleVideoCardHoverStart(index: number): void {
	clearTimeout(hoverTimer);
	hoverTimer = setTimeout(() => {
		const item = videos.value[index];
		if (!item) return;
		if (!canSpeculate()) return;
		if (!trySpendWarmBudget(WARM_COST_FEED_API)) return;
		prefetchPropertyOnIntent(item.property.commercialId);
		prefetchReelPosterOnIntent(item.thumbnailUrl);
	}, HOVER_INTENT_MS);
}

function handleVideoCardHoverEnd(): void {
	clearTimeout(hoverTimer);
}

onUnmounted(() => clearTimeout(hoverTimer));

// Pointerdown fires ~100-200ms before click on touch (finger down vs lift):
// starting the warm there is free head start for the same confirmed intent.
// No mobile o MESMO gesto inicia o scroll do carrossel — sem o guard, cada
// swipe pagava o prefetch completo de um card aleatório. O warm só dispara
// se o dedo ficar parado ~120ms (tap real); arrastou antes, cancela.
const INTENT_HOLD_MS = 120;
let intentTimer: ReturnType<typeof setTimeout> | undefined;

function fireVideoCardIntent(index: number): void {
	const item = videos.value[index];
	if (!item) return;
	prefetchVideoOnIntent(item.videoUrl, item.prefetchVariants);
	prefetchReelPosterOnIntent(item.thumbnailUrl);
	// On touch there is no hover phase, so this is the first (and only)
	// chance to start the property payload before the navigation.
	prefetchPropertyOnIntent(item.property.commercialId);
}

let activeIntentCancel: (() => void) | null = null;

function handleVideoCardIntent(index: number): void {
	// Um gesto por vez: multi-touch ou um segundo pointerdown antes do
	// pointerup anterior derruba os listeners do gesto antigo.
	activeIntentCancel?.();
	clearTimeout(intentTimer);
	intentTimer = setTimeout(() => fireVideoCardIntent(index), INTENT_HOLD_MS);
	const cancel = () => {
		if (activeIntentCancel === cancel) activeIntentCancel = null;
		clearTimeout(intentTimer);
		window.removeEventListener('pointermove', onMove);
		window.removeEventListener('pointerup', onUp);
		window.removeEventListener('pointercancel', cancel);
	};
	let startX: number | null = null;
	let startY: number | null = null;
	const onMove = (e: PointerEvent) => {
		if (startX === null || startY === null) {
			startX = e.clientX;
			startY = e.clientY;
			return;
		}
		if (Math.hypot(e.clientX - startX, e.clientY - startY) > 10) cancel();
	};
	const onUp = () => {
		// Soltou antes do hold: tap rápido — dispara já, o clique vem aí.
		clearTimeout(intentTimer);
		fireVideoCardIntent(index);
		cancel();
	};
	activeIntentCancel = cancel;
	window.addEventListener('pointermove', onMove);
	window.addEventListener('pointerup', onUp, { once: true });
	window.addEventListener('pointercancel', cancel, { once: true });
}

onUnmounted(() => clearTimeout(intentTimer));

// 2s of watched preview = intent without any touch (the only signal the
// mobile autoplay carousel produces). The video is already streaming into
// cache by playing; what is still cold is the API payload (small, always
// worth it) and the poster (bigger, only on networks cleared to speculate).
function handleVideoCardEngaged(index: number): void {
	const item = videos.value[index];
	if (!item) return;
	// 2s de preview assistido e o sinal de intencao do mobile (o indice do
	// carrossel nao muda em quem assiste o primeiro card).
	prefetchPropertyOnIntent(item.property.commercialId);
	if (canSpeculate()) prefetchReelPosterOnIntent(item.thumbnailUrl);
}

function handleVideoCardClick(index: number): void {
	const item = videos.value[index];
	if (!item) return;

	// Click = confirmed intent: pull the video's first chunk while the SPA
	// navigation and feed mount happen, so the player finds it locally.
	prefetchVideoOnIntent(item.videoUrl, item.prefetchVariants);

	const { commercialId } = item.property;

	if (!props.preserveFeedEntryContext) {
		openShortsFeed({ commercialId, clickedIndex: index, entryItem: item });
		return;
	}

	// Semeia o feed com a lista COMPLETA (vídeos + imagens), começando após o vídeo
	// clicado — localizado por `commercialId` na lista bruta `allVideos`, pois a lista
	// visual (`videos`, só-vídeos) diverge quando há imagens antes do clicado. O clicado
	// entra como `target` prependado na página de shorts; imagens posteriores são mantidas.
	const fullIndex = allVideos.value.findIndex(
		video => video.property.commercialId === commercialId
	);
	openShortsFeed({
		commercialId,
		clickedIndex: index,
		entryItem: item,
		entryContext: {
			items: fullIndex >= 0 ? (allVideos.value || []).slice(fullIndex + 1) : [],
			filters: appliedFeedFilters.value ?? props.filters ?? {},
			page: props.fetchPage,
			perPage: feedEntryPerPage.value ?? FEED_ENTRY_PER_PAGE,
			hasMore: feedHasMore.value,
			allWatched: feedAllWatched.value
		},
		queryFilters: props.filters ?? {}
	});
}

function handleCtaClick(): void {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: 'Explorar todos os Shorts',
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: props.ctaSectionName,
		[MixpanelProps.DESTINATION_URL]: SHORTS_HOME_PATH
	});
	navigateTo(SHORTS_HOME_PATH);
}

function getVideoCardPosition(index: number): number {
	if (props.videoCardPositionMode !== 'visible') {
		return index;
	}

	const currentCarouselItemIndex =
		sliderRef.value?.currentCarouselItemIndex ?? 0;

	return Math.max(1, index - currentCarouselItemIndex + 1);
}
</script>

<template>
	<SectionSkeleton
		v-if="shouldShowSection && !isPreloaded"
		:title="title"
		:variant="variant"
		:hide-bottom-border="props.hideBottomBorder"
	/>

	<section v-else-if="shouldShowSection" ref="sectionRef" :class="sectionClass">
		<StandardSlider
			ref="sliderRef"
			:items="videos"
			:is-loading="false"
			:carousel-item-class="carouselItemClass"
			:carousel-options="carouselOptions"
			:limit-photos="false"
			:section-name="props.sliderSectionName"
			:container-class="containerClass"
			:footer-class="footerClass"
			:carousel-content-class="styles.carouselContentClass"
			:selector-variant="styles.selectorVariant"
			:selector-arrows-variant="styles.selectorArrowsVariant"
			:show-navigation-arrows="false"
		>
			<template #header>
				<header :class="headerClass">
					<div class="flex w-full flex-col items-start justify-between">
						<div v-if="displayedTitle" class="flex items-center gap-3">
							<IconMaterialSymbolsSlowMotionVideo
								v-if="showTitleIcon"
								:class="cn('size-7 md:size-12', styles.titleText)"
							/>
							<h2 :class="cn(styles.titleFont, styles.titleText)">
								{{ displayedTitle }}
							</h2>
						</div>
					</div>
				</header>
			</template>

			<template #items="{ item, index }">
				<VideoCard
					:property="item.property"
					:thumbnail-url="item.thumbnailUrl"
					:video-url="item.videoUrl"
					:index="index"
					:img-eager-count="posterEagerCount"
					:card-position="getVideoCardPosition(index)"
					:section-name="props.videoCardSectionName"
					:recommendation-type="
						props.enableRecommendationTypeAnalytics
							? recommendationType
							: undefined
					"
					:is-active="
						shouldAutoplay && sliderRef?.currentCarouselItemIndex === index
					"
					:hover-to-play="!isMobileOrTablet"
					:prefetch-variants="item.prefetchVariants"
					:preload-distance="preloadDistanceFor(index)"
					@click="handleVideoCardClick"
					@pointerdown="handleVideoCardIntent(index)"
					@engaged="handleVideoCardEngaged"
					@mouseenter="handleVideoCardHoverStart(index)"
					@mouseleave="handleVideoCardHoverEnd"
				/>
			</template>

			<template #cta>
				<Button
					v-if="styles.showCtaButton"
					:variant="styles.buttonVariant"
					size="responsive"
					class="text-base font-medium"
					@click="handleCtaClick"
				>
					<span>Explorar todos os Shorts</span>
					<IconMaterialSymbolsLightNorthEast
						:class="cn('size-5 xl:size-6', styles.ctaArrowClass)"
					/>
				</Button>
			</template>
		</StandardSlider>
	</section>
</template>
